import { memo, Suspense } from "react";
import { Route, Routes } from "react-router";
import { AuthLayout } from "./layout";
import DashboardLayout from "./layout/DashboardLayout";
import {
  ForgotPassword,
  Login,
  ResetPassword,
  VerifyEmail,
} from "./pages/auth";
import ManageRole from "./pages/manage-role/ManageRole";
import { ProtectedRoute, PublicRoute } from "./routes";
import PermissionRoute from "./routes/PermissionRoute";
import HomeRedirect from "./routes/HomeRedirect";
import { PermissionProvider } from "./context/PermissionContext";
import { SidebarProvider } from "./context/SidebarContext";
import LoadingSpinner from "./components/LoadingSpinner";
import AccessDenied from "./components/AccessDenied";
import { ManagePartners } from "./screens/ManagePartners";
import { ManageLenders } from "./screens/ManageLenders";
import NotFound from "./pages/manage-customer-new/NotFound";
import CustomerProfile from "./pages/customer-profile/CustomerProfile";
// import RentalCalculator from "./pages/rental-calculator/TeamSheet";
import TermSheetDashboard from "./pages/term-sheet/TermSheet";
import CRM from "./pages/crm/CRM";
import ManageSupplier from "./pages/manage-supplier/ManageSupplier";
import { StoreProvider } from "./store/store";
import ManageRequirement from "./pages/manage-requirement/manageRequirement";
import RentalScheduleDashboard from "./pages/rental-schedules/RentalScheduleDashboard";
import NewCustomerDashboard from "./pages/manage-customer/NewDashboard";
import ManageRevenue from "./pages/manage-revenueMIS/RevenueMIS";
import Invoices from "./pages/rental-modules/Invoices";
import PaymentReconciliation from "./pages/payment-reconciliation/PaymentReconciliation";
import RentalCalculatorLayout from "./components/RentalCalculator/RentalCalculatorLayout";
import ManagePurchaseOrder from "./pages/manage-purchase-order/ManagePurchaseOrder";
import PostDisbursement from "./pages/manage-post-disbursement/PostDisbursement";
import ViewPostDisbursementCase from "./pages/manage-post-disbursement/viewPostDisbursement";
import ManagePortfolio from "./pages/manage-portfolio/ManagePortfolio";
import PortfolioRSDetail from "./pages/manage-portfolio/PortfolioRSDetail";
import { CustomerWorkflowProvider } from "./context/CustomerWorkflowContext";
import AdminDashboard from "./pages/admin-dashboard/AdminDashboard";

const App = memo(() => {
  return (
    <StoreProvider>
      <Routes>
        <Route element={<ProtectedRoute />}>
          <Route
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PermissionProvider>
                  <SidebarProvider>
                    <DashboardLayout />
                  </SidebarProvider>
                </PermissionProvider>
              </Suspense>
            }
          >
            {/* Dashboard is accessible to all authenticated users */}
            <Route
              path="/lead"
              element={
                <PermissionRoute
                  permissionKey="canManageLeads"
                  element={<CRM />}
                  fallback={
                    <AccessDenied
                      title="Role Management Restricted"
                      subTitle="You don't have permission to manage leads."
                    />
                  }
                />
              }
            />

          <Route
              path="/admin-dashboard"
              element={
                <PermissionRoute
                  permissionKey="canManageAdminDashboard"
                  element={<AdminDashboard />}
                  fallback={
                    <AccessDenied
                      title="Admin Dashboard Restricted"
                      subTitle="You don't have permission to view the admin dashboard."
                    />
                  }
                />
              }
            />

            {/* For the role management, we'll conditionally render based on permissions */}
            <Route
              path="/manage-role"
              element={
                <PermissionRoute
                  permissionKey="canManageRoles"
                  element={<ManageRole />}
                  fallback={
                    <AccessDenied
                      title="Role Management Restricted"
                      subTitle="You don't have permission to manage roles."
                    />
                  }
                />
              }
            />

            {/* For customer management */}
            <Route
              path="/manage-customer"
              element={
                <PermissionRoute
                  permissionKey="canManageCustomers"
                  element={< NewCustomerDashboard />}
                  fallback={
                    <AccessDenied
                      title="Customer Management Restricted"
                      subTitle="You don't have permission to manage customers."
                    />
                  }
                />
              }
            />

            <Route
              path="/manage-partner"
              element={
                <PermissionRoute
                  permissionKey="canManagePartners"
                  element={<ManagePartners />}
                  fallback={
                    <AccessDenied
                      title="Partner Management Restricted"
                      subTitle="You don't have permission to manage partners."
                    />
                  }
                />
              }
            />

            <Route
              path="/manage-lender"
              element={
                <PermissionRoute
                  permissionKey="canManageLenders"
                  element={<ManageLenders />}
                  fallback={
                    <AccessDenied
                      title="Lender Management Restricted"
                      subTitle="You don't have permission to manage lenders."
                    />
                  }
                />
              }
            />

            <Route
              path="/manage-customer/:customerId"
              element={
                <PermissionRoute
                  permissionKey="canManageCustomers"
                  element={<CustomerWorkflowProvider>
                    <CustomerProfile />
                  </CustomerWorkflowProvider>}
                  fallback={
                    <AccessDenied
                      title="Customer Details Restricted"
                      subTitle="You don't have permission to view customer details."
                    />
                  }
                />
              }
            />

            <Route
              path="/term-sheet"
              // path="/rental-calculator/:customerId"

              element={
                <PermissionRoute
                  permissionKey="canManageTermSheet"
                  element={<TermSheetDashboard />}
                  fallback={
                    <AccessDenied
                      title="Customer Details Restricted"
                      subTitle="You don't have permission to view customer details."
                    />
                  }
                />
              }
            />

            <Route
              path="/rental-calculator"
              // path="/rental-calculator/:customerId"

              element={
                <PermissionRoute
                  permissionKey="canManageCalculator"
                  element={< RentalCalculatorLayout />}
                  fallback={
                    <AccessDenied
                      title="Customer Details Restricted"
                      subTitle="You don't have permission to view customer details."
                    />
                  }
                />
              }
            />



            <Route
              path="/manage-supplier"
              element={
                <PermissionRoute
                  permissionKey="canManageSupplier"
                  element={< ManageSupplier />}
                  fallback={
                    <AccessDenied
                      title="Customer Management Restricted"
                      subTitle="You don't have permission to manage customers."
                    />
                  }
                />
              }
            />

            <Route
              path="/rental-schedule-dashboard"
              // path="/rental-calculator/:customerId"

              element={
                <PermissionRoute
                  permissionKey="canManageRentalSchedule"
                  element={<RentalScheduleDashboard />}
                  fallback={
                    <AccessDenied
                      title="Customer Details Restricted"
                      subTitle="You don't have permission to view customer details."
                    />
                  }
                />
              }
            />
            {/* <Route path="/pos" element={<PurchaseOrders />} /> */}
            {/* <Route path="/pos/:id" element={<PODetail />} /> */}



            <Route
              path="/manage-login"
              element={
                <PermissionRoute
                  permissionKey="canManageLogin"
                  element={<ManageRequirement />}
                  fallback={
                    <AccessDenied
                      title="Login Details Restricted"
                      subTitle="You don't have permission to view Login details."
                    />
                  }
                />
              }
            />

            {/* For customer management */}
            <Route
              path="/manage-supplier"
              element={
                <PermissionRoute
                  permissionKey="canManageSupplier"
                  element={< ManageSupplier />}
                  fallback={
                    <AccessDenied
                      title="Customer Management Restricted"
                      subTitle="You don't have permission to manage customers."
                    />
                  }
                />
              }
            />
            {/* For New customer management */}

            <Route
              path="/manage-purchase-order"
              element={
                <PermissionRoute
                  permissionKey="canManagePurchaseOrder"
                  element={<ManagePurchaseOrder />}
                />
              }
            />



            <Route
              path="/manage-revenue"

              element={
                <PermissionRoute
                  permissionKey="canManageRevenue"
                  element={< ManageRevenue />}
                  fallback={
                    <AccessDenied
                      title="Manage Revenue Restricted"
                      subTitle="You don't have permission to view Manage Revenue."
                    />
                  }
                />
              }
            />

            <Route
              path="/manage-post-disbursement"
              element={
                <PermissionRoute
                  permissionKey="canManagePostDisbursement"
                  element={< PostDisbursement />}
                  fallback={
                    <AccessDenied
                      title="Manage Post Disbursement Restricted"
                      subTitle="You don't have permission to view Manage Post Disbursement."
                    />
                  }
                />
              }
            />
            <Route
              path="/ViewPostDisbursement/:rsId"
              element={
                <PermissionRoute
                  permissionKey="canManagePostDisbursement"
                  element={<ViewPostDisbursementCase />}
                  fallback={
                    <AccessDenied
                      title="Manage Post Disbursement Restricted"
                      subTitle="You don't have permission to view Manage Post Disbursement."
                    />
                  }
                />
              }
            />

            <Route
              path="/manage-portfolio"
              element={
                <PermissionRoute
                  permissionKey="canManagePortfolio"
                  element={< ManagePortfolio />}
                  fallback={
                    <AccessDenied
                      title="Manage Portfolio Restricted"
                      subTitle="You don't have permission to view Manage Portfolio."
                    />
                  }
                />
              }
            />

            <Route
              path="/manage-portfolio/:rsId"
              element={
                <PermissionRoute
                  permissionKey="canManagePortfolio"
                  element={<PortfolioRSDetail />}
                  fallback={
                    <AccessDenied
                      title="Manage Portfolio Restricted"
                      subTitle="You don't have permission to view Manage Portfolio."
                    />
                  }
                />
              }
            />


            {/* For rental invoice management */}
            <Route
              path="/manage-rental-invoice"

              element={
                <PermissionRoute
                  permissionKey="canManageRentalInvoice"
                  element={< Invoices />}
                  fallback={
                    <AccessDenied
                      title="Manage Rental Invoice Restricted"
                      subTitle="You don't have permission to view Manage Rental Invoice."
                    />
                  }
                />
              }
            />

            <Route
              path="/invoice-payment"
              element={
                <PermissionRoute
                  permissionKey="canManageRentalInvoice"
                  element={<PaymentReconciliation />}
                  fallback={
                    <AccessDenied
                      title="Invoice Payment Restricted"
                      subTitle="You don't have permission to manage invoice payments."
                    />
                  }
                />
              }
            />

            {/* <Route path="/pos" element={<PurchaseOrders />} /> */}
            {/* <Route path="/pos/:id" element={<PODetail />} /> */}



            {/* <Route
              path="/lead"
              element={
                <PermissionRoute
                  permissionKey="canManageLeads"
                  element={<CRM />}
                  fallback={
                    <AccessDenied
                      title="Leads Details Restricted"
                      subTitle="You don't have permission to view Leads details."
                    />
                  }
                /> 
              }
            /> */}


            <Route path="/" element={<HomeRedirect />} />
          </Route>





          <Route path="*" element={<NotFound />} />

        </Route>
        <Route element={<PublicRoute />}>
          <Route element={<AuthLayout />}>
            <Route path="login" element={<Login />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="reset-password/:id" element={<ResetPassword />} />
            <Route path="verify-email" element={<VerifyEmail />} />
          </Route>
        </Route>
      </Routes>
    </StoreProvider>

  );
});

export default App;
